import { useId } from 'react'
import type { ReactElement, SelectHTMLAttributes } from 'react'

import styles from './select.module.scss'

export type SelectOption = {
  value: string
  label: string
  /**
   * Offered but not choosable — a work-center type another group already holds.
   * It stays in the list on purpose: removing it answers "why is Marmita not
   * here?" with silence, while a disabled row says who has it.
   */
  disabled?: boolean
}

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string
  options: Array<SelectOption>
  /** The empty first row ("Elige un tipo…"), rendered with an empty value. */
  placeholder?: string
  /**
   * The label stays a real `<label>` but is visually hidden — for a select inside
   * a table row, where the column header already says what it holds and a
   * visible label per row is noise. A screen reader and `getByLabelText` still
   * find it.
   */
  hideLabel?: boolean
}

/** No per-field `error` here either — see the note on `InputText`. */
export const Select = ({
  label,
  options,
  placeholder,
  hideLabel = false,
  id,
  className,
  ...rest
}: SelectProps): ReactElement => {
  const generatedId = useId()
  const selectId = id ?? generatedId

  return (
    <div className={[styles.select, className ?? ''].filter(Boolean).join(' ')}>
      <label className={hideLabel ? 'visually-hidden' : styles.select__label} htmlFor={selectId}>{label}</label>
      <select id={selectId} className={styles.select__field} {...rest}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}
